import React, { useMemo } from 'react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import styled, { css } from 'styled-components'

import { ChartTooltipBody } from '../chart-tooltip'
import {
  LemonadeDonutChartLegendPosition,
  LemonadeDonutChartProps,
} from './donut-chart.types'

const ChartContainer = styled.div`
  & .recharts-wrapper,
  & .recharts-surface,
  & path,
  & g {
    outline: none;
  }
`

const AUTO_LEGEND_MIN_WIDTH_PX = 180

const Row = styled.div<{ $position: LemonadeDonutChartLegendPosition }>`
  display: flex;
  align-items: center;
  ${({ $position, theme }) => {
    if ($position === 'side') {
      return css`
        flex-direction: row;
        gap: ${theme.sizes.l};
      `
    }
    if ($position === 'bottom') {
      return css`
        flex-direction: column;
      `
    }
    return css`
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      gap: ${theme.sizes.l};
    `
  }}
`

const ChartSlot = styled.div<{
  $position: LemonadeDonutChartLegendPosition
  $h: number
}>`
  ${({ $position, $h }) => {
    if ($position === 'side' || $position === 'auto') {
      return css`
        flex: 0 0 ${$h}px;
        width: ${$h}px;
      `
    }
    return css`
      width: 100%;
    `
  }}
`

const LegendSlot = styled.div<{ $position: LemonadeDonutChartLegendPosition }>`
  ${({ $position }) => {
    if ($position === 'side') {
      return css`
        flex: 1 1 auto;
        min-width: 0;
      `
    }
    if ($position === 'auto') {
      return css`
        flex: 1 1 ${AUTO_LEGEND_MIN_WIDTH_PX}px;
        min-width: 0;
      `
    }
    return css`
      width: 100%;
    `
  }}
`

const BASE_HUE = 263
const BASE_SAT = 100
const BASE_LIGHT = 65
const MAX_LIGHT = 88

function generateShades(count: number): string[] {
  if (count <= 1) return [`hsl(${BASE_HUE}, ${BASE_SAT}%, ${BASE_LIGHT}%)`]

  return Array.from({ length: count }, (_, i) => {
    const lightness = BASE_LIGHT + ((MAX_LIGHT - BASE_LIGHT) * i) / (count - 1)

    return `hsl(${BASE_HUE}, ${BASE_SAT}%, ${Math.round(lightness)}%)`
  })
}

const DEFAULT_INNER_RADIUS_RATIO = 0.6

export const LemonadeDonutChart = ({
  data,
  nameKey,
  valueKey,
  colors,
  tickFormatter,
  h = 200,
  w,
  innerRadiusRatio = DEFAULT_INNER_RADIUS_RATIO,
  legend,
  legendPosition = 'bottom',
}: LemonadeDonutChartProps) => {
  const slices = useMemo(
    () => colors ?? generateShades(data.length),
    [colors, data.length]
  )

  // Sum the slice values so the tooltip can render each slice's percentage
  // share. Recharts doesn't put `percent` on the Pie tooltip payload itself.
  const total = useMemo(
    () =>
      data.reduce((sum, row) => {
        const v = row[valueKey]
        return sum + (typeof v === 'number' ? v : 0)
      }, 0),
    [data, valueKey]
  )

  const outerRadius = '100%'
  const innerRadius = `${Math.round(innerRadiusRatio * 100)}%`

  const chart = (
    <ChartContainer>
      <ResponsiveContainer width={w ?? '100%'} height={h}>
        <PieChart>
          <Tooltip
            content={
              <ChartTooltipBody formatter={tickFormatter} total={total} />
            }
            cursor={{ fill: 'rgba(139, 74, 255, 0.05)' }}
          />
          <Pie
            data={data}
            dataKey={valueKey}
            nameKey={nameKey}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={0}
            stroke="#ffffff"
            strokeWidth={2}
            isAnimationActive
            animationDuration={800}
            animationEasing="ease-out"
          >
            {data.map((_, idx) => (
              <Cell key={idx} fill={slices[idx % slices.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  )

  if (!legend) return chart

  return (
    <Row $position={legendPosition}>
      <ChartSlot $position={legendPosition} $h={h}>
        {chart}
      </ChartSlot>
      <LegendSlot $position={legendPosition}>{legend}</LegendSlot>
    </Row>
  )
}
